import { dataDictionaryActions } from './data-dictionary.actions';
import { removeItemFromArray } from '../../util/util';

export const initialState = {
  dictionaryList: [],
  dictionaryDetails: null,
  schemaData: null,
  fullSchemaData: null,
  // Per-schema load state for the Schema Selection checklist:
  // [{schema, status: 'loading'|'done'|'error', counts, error}]
  schemaLoadProgress: [],
  failedSchemas: [],
  isLoading: [],
  error: [],
  saveStatus: null,
  importSchemaResponse: null,
  importSchemaLoading: false,
  importSchemaError: null,
  userList: [],
  techOwnerList: [],
  pendingChanges: [],
  refreshResult: null,
  isRefreshing: false,
  workflowActionResult: null,
  rollbackResult: null,
  rollbackHistory: [],
  deleteResult: null,
  missingItems: null,
  isLoadingMissing: false,
  importMissingResult: null,
  isImportingMissing: false,
};

export function dataDictionaryReducer(state = initialState, action) {
  let status;

  switch (action.type) {
    //-------Get Full Schema Request-------------
    case dataDictionaryActions.GET_FULL_SCHEMA_REQUEST:
      return {
        ...state,
        fullSchemaData: null,
        // One row per selected schema, seeded as in-flight. Requests run in
        // parallel, so they are all genuinely reading at once — there is no
        // "queued" state to show.
        schemaLoadProgress: ((action.payload && action.payload.schemas) || [])
          .map((schema) => ({schema, status: 'loading', counts: null, error: null})),
        isLoading: [...state.isLoading, 'fullschema']
      };

    //-------Get Full Schema Progress (one schema landed)----
    case dataDictionaryActions.GET_FULL_SCHEMA_PROGRESS:
      return {
        ...state,
        schemaLoadProgress: (state.schemaLoadProgress || []).map((row) =>
          row.schema === action.payload.schema
            ? {
              ...row,
              status: action.payload.status,
              counts: action.payload.counts || null,
              error: action.payload.error || null,
            }
            : row
        )
      };

    //-------Get Full Schema Success-------------
    case dataDictionaryActions.GET_FULL_SCHEMA_SUCCESS:
      return {
        ...state,
        fullSchemaData: action.payload.response,
        // Non-empty when some schemas could not be read. The objects that did load
        // stay usable; the wizard blocks Next until the selection is fixed.
        failedSchemas: action.payload.failedSchemas || [],
        isLoading: removeItemFromArray(state.isLoading, 'fullschema')
      };

    //-------Get Full Schema Failure-------------
    case dataDictionaryActions.GET_FULL_SCHEMA_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        fullSchemaData: null,
        schemaLoadProgress: (state.schemaLoadProgress || [])
          .map((row) => (row.status === 'loading' ? {...row, status: 'error'} : row)),
        isLoading: removeItemFromArray(state.isLoading, 'fullschema'),
        error: [...state.error, status]
      };

    //-------Get Schema Request-------------
    case dataDictionaryActions.GET_SCHEMA_REQUEST:
      return {
        ...state,
        schemaData: null,
        isLoading: [...state.isLoading, 'schema']
      };

    //-------Get Schema Success-------------
    case dataDictionaryActions.GET_SCHEMA_SUCCESS:
      return {
        ...state,
        schemaData: action.payload.response,
        isLoading: removeItemFromArray(state.isLoading, 'schema')
      };

    //-------Get Schema Failure-------------
    case dataDictionaryActions.GET_SCHEMA_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        schemaData: null,
        isLoading: removeItemFromArray(state.isLoading, 'schema'),
        error: [...state.error, status]
      };

    //-------Save Dictionary Request-------------
    case dataDictionaryActions.SAVE_DICTIONARY_REQUEST:
      return {
        ...state,
        saveStatus: null,
        isLoading: [...state.isLoading, 'savedictionary']
      };

    //-------Save Dictionary Success-------------
    case dataDictionaryActions.SAVE_DICTIONARY_SUCCESS:
      return {
        ...state,
        saveStatus: action.payload,
        isLoading: removeItemFromArray(state.isLoading, 'savedictionary')
      };

    //-------Save Dictionary Failure-------------
    case dataDictionaryActions.SAVE_DICTIONARY_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        saveStatus: null,
        isLoading: removeItemFromArray(state.isLoading, 'savedictionary'),
        error: [...state.error, status]
      };

    //-------Load Dictionary List Request-------------
    case dataDictionaryActions.LOAD_DICTIONARY_LIST_REQUEST:
      return {
        ...state,
        isLoading: [...state.isLoading, 'dictionarylist']
      };

    //-------Load Dictionary List Success-------------
    case dataDictionaryActions.LOAD_DICTIONARY_LIST_SUCCESS:
      return {
        ...state,
        dictionaryList: action.payload.response,
        isLoading: removeItemFromArray(state.isLoading, 'dictionarylist')
      };

    //-------Load Dictionary List Failure-------------
    case dataDictionaryActions.LOAD_DICTIONARY_LIST_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'dictionarylist'),
        error: [...state.error, status]
      };

    //-------Delete Dictionary Request-------------
    case dataDictionaryActions.DELETE_DICTIONARY_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'deletedictionary']
      };

    //-------Delete Dictionary Success-------------
    case dataDictionaryActions.DELETE_DICTIONARY_SUCCESS:
      return {
        ...state,
        deleteResult: action.payload,
        isLoading: removeItemFromArray(state.isLoading, 'deletedictionary')
      };

    //-------Delete Dictionary Failure-------------
    case dataDictionaryActions.DELETE_DICTIONARY_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'deletedictionary'),
        error: [...state.error, status]
      };

    //-------Load Dictionary Details Request-------------
    case dataDictionaryActions.LOAD_DICTIONARY_DETAILS_REQUEST:
      return {
        ...state,
        dictionaryDetails: null,
        isLoading: [...state.isLoading, 'dictionarydetails']
      };

    //-------Load Dictionary Details Success-------------
    case dataDictionaryActions.LOAD_DICTIONARY_DETAILS_SUCCESS:
      return {
        ...state,
        dictionaryDetails: action.payload.response,
        isLoading: removeItemFromArray(state.isLoading, 'dictionarydetails')
      };

    //-------Load Dictionary Details Failure-------------
    case dataDictionaryActions.LOAD_DICTIONARY_DETAILS_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        dictionaryDetails: null,
        isLoading: removeItemFromArray(state.isLoading, 'dictionarydetails'),
        error: [...state.error, status]
      };

    //-------Update Dictionary Instance Request-------------
    case dataDictionaryActions.UPDATE_DICTIONARY_INSTANCE_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'updatedictionary']
      };

    //-------Update Dictionary Instance Success-------------
    case dataDictionaryActions.UPDATE_DICTIONARY_INSTANCE_SUCCESS:
      return {
        ...state,
        dictionaryDetails: action.payload.response,
        isLoading: removeItemFromArray(state.isLoading, 'updatedictionary')
      };

    //-------Update Dictionary Instance Failure-------------
    case dataDictionaryActions.UPDATE_DICTIONARY_INSTANCE_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        isLoading: removeItemFromArray(state.isLoading, 'updatedictionary'),
        error: [...state.error, status]
      };

    //-------Import Schema Request-------------
    case dataDictionaryActions.IMPORT_SCHEMA_REQUEST:
      return {
        ...state,
        error: [],
        importSchemaResponse: null,
        importSchemaLoading: true,
        importSchemaError: null,
        isLoading: [...state.isLoading, 'importschema']
      };

    //-------Import Schema Success-------------
    case dataDictionaryActions.IMPORT_SCHEMA_SUCCESS:
      return {
        ...state,
        importSchemaResponse: action.payload,
        importSchemaLoading: false,
        importSchemaError: null,
        isLoading: removeItemFromArray(state.isLoading, 'importschema')
      };

    //-------Import Schema Failure-------------
    case dataDictionaryActions.IMPORT_SCHEMA_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        importSchemaResponse: null,
        importSchemaLoading: false,
        importSchemaError: action.payload,
        isLoading: removeItemFromArray(state.isLoading, 'importschema'),
        error: [...state.error, status]
      };

    //-------Fetch User List Request-------------
    case dataDictionaryActions.FETCH_USER_LIST_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'userlist']
      };

    //-------Fetch User List Success-------------
    case dataDictionaryActions.FETCH_USER_LIST_SUCCESS:
      const userList = action.payload.response.value || [];
      return {
        ...state,
        userList: userList,
        isLoading: removeItemFromArray(state.isLoading, 'userlist')
      };

    //-------Fetch User List Failure-------------
    case dataDictionaryActions.FETCH_USER_LIST_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        userList: [],
        isLoading: removeItemFromArray(state.isLoading, 'userlist'),
        error: [...state.error, status]
      };

    //-------Fetch Tech Owner List Request-------------
    case dataDictionaryActions.FETCH_TECH_OWNER_LIST_REQUEST:
      return {
        ...state,
        error: [],
        isLoading: [...state.isLoading, 'techownerlist']
      };

    //-------Fetch Tech Owner List Success-------------
    case dataDictionaryActions.FETCH_TECH_OWNER_LIST_SUCCESS:
      const techOwnerList = action.payload.response.value || [];
      return {
        ...state,
        techOwnerList: techOwnerList,
        isLoading: removeItemFromArray(state.isLoading, 'techownerlist')
      };

    //-------Fetch Tech Owner List Failure-------------
    case dataDictionaryActions.FETCH_TECH_OWNER_LIST_FAILURE:
      status = '';
      action.payload.response === undefined ? status = '404' : status = action.payload.response.status;
      return {
        ...state,
        techOwnerList: [],
        isLoading: removeItemFromArray(state.isLoading, 'techownerlist'),
        error: [...state.error, status]
      };

    //-------Refresh With Diff Request-------------
    case dataDictionaryActions.REFRESH_WITH_DIFF_REQUEST:
      return { ...state, isRefreshing: true, refreshResult: null };

    //-------Refresh With Diff Success-------------
    case dataDictionaryActions.REFRESH_WITH_DIFF_SUCCESS:
      return { ...state, isRefreshing: false, refreshResult: action.payload.response };

    //-------Refresh With Diff Failure-------------
    case dataDictionaryActions.REFRESH_WITH_DIFF_FAILURE:
      return { ...state, isRefreshing: false, refreshResult: null };

    //-------Load Pending Changes Request-------------
    case dataDictionaryActions.LOAD_PENDING_CHANGES_REQUEST:
      return { ...state, isLoading: [...state.isLoading, 'pendingchanges'] };

    //-------Load Pending Changes Success-------------
    case dataDictionaryActions.LOAD_PENDING_CHANGES_SUCCESS:
      return {
        ...state,
        pendingChanges: (action.payload.response && action.payload.response.rows) || [],
        isLoading: removeItemFromArray(state.isLoading, 'pendingchanges'),
      };

    //-------Load Pending Changes Failure-------------
    case dataDictionaryActions.LOAD_PENDING_CHANGES_FAILURE:
      return { ...state, pendingChanges: [], isLoading: removeItemFromArray(state.isLoading, 'pendingchanges') };

    //-------Workflow Action Request-------------
    case dataDictionaryActions.WORKFLOW_ACTION_REQUEST:
      return { ...state, isLoading: [...state.isLoading, 'workflowaction'] };

    //-------Workflow Action Success-------------
    case dataDictionaryActions.WORKFLOW_ACTION_SUCCESS:
      return {
        ...state,
        workflowActionResult: action.payload.response,
        isLoading: removeItemFromArray(state.isLoading, 'workflowaction'),
      };

    //-------Workflow Action Failure-------------
    case dataDictionaryActions.WORKFLOW_ACTION_FAILURE:
      return { ...state, workflowActionResult: null, isLoading: removeItemFromArray(state.isLoading, 'workflowaction') };

    //-------Rollback Change Request Request-------------
    case dataDictionaryActions.ROLLBACK_CHANGE_REQUEST_REQUEST:
      return { ...state, rollbackResult: null, isLoading: [...state.isLoading, 'rollback'] };

    //-------Rollback Change Request Success-------------
    case dataDictionaryActions.ROLLBACK_CHANGE_REQUEST_SUCCESS:
      return { ...state, rollbackResult: action.payload.response, isLoading: removeItemFromArray(state.isLoading, 'rollback') };

    //-------Rollback Change Request Failure-------------
    case dataDictionaryActions.ROLLBACK_CHANGE_REQUEST_FAILURE:
      return { ...state, rollbackResult: null, isLoading: removeItemFromArray(state.isLoading, 'rollback') };

    //-------Load Rollback History Request-------------
    case dataDictionaryActions.LOAD_ROLLBACK_HISTORY_REQUEST:
      return { ...state, rollbackHistory: [], isLoading: [...state.isLoading, 'rollbackhistory'] };

    //-------Load Rollback History Success-------------
    case dataDictionaryActions.LOAD_ROLLBACK_HISTORY_SUCCESS:
      return { ...state, rollbackHistory: action.payload.response || [], isLoading: removeItemFromArray(state.isLoading, 'rollbackhistory') };

    //-------Load Rollback History Failure-------------
    case dataDictionaryActions.LOAD_ROLLBACK_HISTORY_FAILURE:
      return { ...state, rollbackHistory: [], isLoading: removeItemFromArray(state.isLoading, 'rollbackhistory') };

    //-------Load Missing Request-------------
    case dataDictionaryActions.LOAD_MISSING_REQUEST:
      return { ...state, isLoadingMissing: true, missingItems: null };

    //-------Load Missing Success-------------
    case dataDictionaryActions.LOAD_MISSING_SUCCESS:
      return { ...state, isLoadingMissing: false, missingItems: action.payload.response };

    //-------Load Missing Failure-------------
    case dataDictionaryActions.LOAD_MISSING_FAILURE:
      return { ...state, isLoadingMissing: false, missingItems: null };

    //-------Import Missing Request-------------
    case dataDictionaryActions.IMPORT_MISSING_REQUEST:
      return { ...state, isImportingMissing: true, importMissingResult: null };

    //-------Import Missing Success-------------
    case dataDictionaryActions.IMPORT_MISSING_SUCCESS:
      return { ...state, isImportingMissing: false, importMissingResult: action.payload.response };

    //-------Import Missing Failure-------------
    case dataDictionaryActions.IMPORT_MISSING_FAILURE:
      return { ...state, isImportingMissing: false, importMissingResult: null };

    //-------Preview Missing Attributes Request-------------
    case dataDictionaryActions.PREVIEW_MISSING_ATTRIBUTES_REQUEST:
      return { ...state, isLoadingMissing: true };

    //-------Preview Missing Attributes Success-------------
    case dataDictionaryActions.PREVIEW_MISSING_ATTRIBUTES_SUCCESS:
      return {
        ...state,
        isLoadingMissing: false,
        missingItems: state.missingItems
          ? {
              ...state.missingItems,
              attributesByCategory: action.payload.response?.attributesByCategory || {},
              scope: action.payload.response?.scope || state.missingItems.scope,
            }
          : state.missingItems,
      };

    //-------Preview Missing Attributes Failure-------------
    case dataDictionaryActions.PREVIEW_MISSING_ATTRIBUTES_FAILURE:
      return { ...state, isLoadingMissing: false };

    default:
      return state;
  }
}
